import type { Dispatch, FC, SetStateAction } from "react";
import { use, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";
import { Document } from "@/graphql/__generated__/graphql";
import { useTranslation } from "react-i18next";
import { DocumentContext, DocumentContextType } from "@/context/DocumentProvider";
import CircleLoader from "../shared/loaders/CircleLoader";
import { Composer, ThreadWelcome, ChatMessagesList, PendingQuestionSection } from "./sub-components";
import { ChatStatus } from "@/constants/enums";
import {
  useScrollManagement,
  usePendingQuestion,
  useChatMessages,
  useChatStreaming,
  useChatActions,
  useChatSSE,
} from "@/hooks/chat";
import { useDocumentChatRuntime } from "@/store/documentChatRuntime";

/**
 * Props for the AIChatThread component
 */
interface AIChatThreadProps {
  /** Array of tender documents available for chat context */
  tenderDocumentsData: Document[];
  /** Whether the documents view panel is currently shown */
  showDocumentsView: boolean;
  /** Function to toggle the documents view panel */
  setShowDocumentsView: Dispatch<SetStateAction<boolean>>;
}

/**
 * AI-powered chat thread component for document-based conversations.
 * Provides a complete chat interface with streaming responses, message history, and document context.
 *
 * Features:
 * - Real-time streaming chat responses via SSE
 * - Message history persistence and hydration from database
 * - Document context selection and management
 * - Thinking indicators and tool call status displays
 * - Copy/refresh functionality for messages
 * - Welcome screen with suggested prompts
 * - Auto-scroll behavior and scroll-to-bottom controls
 *
 * @component
 * @example
 * // Basic usage with tender documents
 * <AIChatThread
 *   tenderDocumentsData={documents}
 *   showDocumentsView={false}
 *   setShowDocumentsView={setShowDocuments}
 * />
 *
 * @example
 * // With documents view panel open
 * <AIChatThread
 *   tenderDocumentsData={documents}
 *   showDocumentsView={true}
 *   setShowDocumentsView={setShowDocuments}
 * />
 */
export const AIChatThread: FC<AIChatThreadProps> = (props: AIChatThreadProps) => {
  const { tenderDocumentsData, showDocumentsView, setShowDocumentsView } = props;

  // Document context for text selection functionality
  const { setSelectedText } = use(DocumentContext) as DocumentContextType;

  // Custom hooks for state management
  const {
    pendingQuestion,
    pushUpPendingQuestion,
    setPendingQuestionWithPushUp,
    clearPendingQuestion,
  } = usePendingQuestion();

  const {
    scrollRef,
    isAtBottom,
    scrollContainerHeight,
    scrollToBottom,
    scrollElementToTop,
  } = useScrollManagement({ pushUpPendingQuestion });

  // Chat messages management
  const { messages, loading: dbMessagesLoading, refetch: refetchMessages, hasMessages, aiImportId, noDocuments } =
    useChatMessages({ tenderDocumentsData });

  // Streaming state management
  const streamStatus = useDocumentChatRuntime((s) => s.status);
  const isStreaming = streamStatus === ChatStatus.CONNECTING || streamStatus === ChatStatus.OPEN;

  // Track when we're doing a post-stream refetch to prevent scroll jumps
  const isRefetchingRef = useRef(false);

  const streamingState = useChatStreaming({
    isStreaming,
    pendingQuestion,
    clearPendingQuestion,
    refetchMessages,
    scrollRef: scrollRef as React.RefObject<HTMLDivElement | null>,
    onRefetchStateChange: (isRefetching) => {
      isRefetchingRef.current = isRefetching;
    },
  });

  // React hooks for internationalization and SSE
  const { t } = useTranslation(["components", "common"]);
  // Single shared SSE instance - all send/reset/cancel operations use the same sessionRef
  const { send, reset } = useChatSSE();

  // Chat actions (resend, refresh, question sent)
  const { handleRefresh, handleResend, handleQuestionSent } = useChatActions({
    aiImportId,
    scrollToBottom,
    setPendingQuestionWithPushUp,
    send, // Pass shared send function
  });

  // Computed values for component state
  const hasConversation = useMemo(
    () =>
      hasMessages ||
      pendingQuestion !== null ||
      (streamingState.streamingContent?.length ?? 0) > 0,
    [hasMessages, pendingQuestion, streamingState.streamingContent]
  );

  /**
   * Unified scroll management effect - handles all dynamic scrolling behavior.
   * Consolidates: auto-scroll during streaming, pending question positioning, and scroll position tracking.
   */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Auto-scroll to bottom during streaming (if user is already at bottom and no pending question)
    if (!pendingQuestion && isAtBottom && streamingState.streamingContent) {
      scrollToBottom();
    }
  }, [streamingState.streamingContent, isAtBottom, pendingQuestion, scrollToBottom]);

  /**
   * Initial scroll position setup - scrolls to bottom when messages first load.
   * Skips scrolling when showing a new pending question at top or during post-stream refetch.
   */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (
      !el ||
      dbMessagesLoading ||
      (pendingQuestion && pushUpPendingQuestion) ||
      isRefetchingRef.current
    )
      return;

    if (hasMessages || hasConversation) {
      el.scrollTop = el.scrollHeight;
    }
  }, [
    messages,
    dbMessagesLoading,
    hasMessages,
    hasConversation,
    pendingQuestion,
    pushUpPendingQuestion,
  ]);

  // Effect: Initialize component by fetching server messages on mount
  useEffect(() => {
    if (noDocuments) return; // Skip if no documents available
    refetchMessages(); // Fetch latest messages from server
  }, [noDocuments, refetchMessages]);

  /**
   * Cleanup on component unmount to prevent memory leaks.
   * Cancels pending operations and resets state.
   */
  useEffect(() => {
    return () => {
      // Reset streaming state and close any open connections
      reset();
      // Clear any selected text to prevent stale state
      if (setSelectedText) {
        setSelectedText(undefined);
      }
      clearPendingQuestion();
    };
  }, [reset, setSelectedText, clearPendingQuestion]);

  // Render only the loader during initial/hydration loading
  if (dbMessagesLoading) {
    return (
      <div
        className={cn("bg-background flex h-full flex-col w-full items-center")}
        style={{
          ["--thread-max-width" as string]: "50rem",
          ["--thread-padding-x" as string]: "1rem",
        }}
      >
        <div
          ref={scrollRef}
          className={cn(
            "w-full max-w-(--thread-max-width) relative flex min-w-0 flex-1 flex-col gap-6 overflow-y-scroll overflow-x-hidden"
          )}
        >
          <div className="flex h-full w-full items-center justify-center">
            <CircleLoader height={40} width={40} strokeWidth={2} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn("bg-background flex h-full flex-col w-full items-center", hasConversation && "pt-4")}
      style={{
        ["--thread-max-width" as string]: "50rem",
        ["--thread-padding-x" as string]: "1rem",
      }}
    >
      <div
        ref={scrollRef}
        className={cn(
          "w-full max-w-(--thread-max-width) relative flex min-w-0 flex-1 flex-col gap-6 overflow-y-scroll overflow-x-hidden",
          hasConversation && "mr-0"
        )}
      >
        {!hasConversation ? (
          /* Show welcome screen only when loading is complete and there are no messages */
          <ThreadWelcome t={t} />
        ) : (
          /* Show chat messages and conversation UI */
          <div
            className={cn(
              "flex max-w-(--thread-max-width) flex-col gap-6 px-(--thread-padding-x) min-w-0",
              hasConversation && "pb-6"
            )}
            style={
              pushUpPendingQuestion && isStreaming
                ? { paddingBottom: Math.max(scrollContainerHeight * 0.4, 200) }
                : undefined
            }
          >
            {/* Show messages from DB */}
            <ChatMessagesList messages={messages} t={t} onResend={handleResend} />

            {/* Show pending question and streaming response */}
            <PendingQuestionSection
              pendingQuestion={pendingQuestion}
              aiImportId={aiImportId}
              streamingState={streamingState}
              t={t}
              onRefresh={handleRefresh}
              onResend={handleResend}
              scrollElementToTop={scrollElementToTop}
              pushUpPendingQuestion={pushUpPendingQuestion}
              scrollContainerHeight={scrollContainerHeight}
            />
          </div>
        )}
      </div>

      {/* Chat Input */}
      <Composer
        tenderDocumentsData={tenderDocumentsData}
        showDocumentsView={showDocumentsView}
        setShowDocumentsView={setShowDocumentsView}
        hasConversation={hasConversation ?? false}
        onQuestionSent={handleQuestionSent}
        aiImportId={aiImportId}
        isAtBottom={isAtBottom}
        scrollRef={scrollRef}
        send={send}
        reset={reset}
      />
    </div>
  );
};
